import type { SmartFormConfig } from '@pesto/ui/features/SmartForm/types';

import { useMutationWithToast } from '@pesto/ui';
import { CurrencyField } from '@pesto/ui/components/Forms/CurrencyField';
import { z } from 'zod';

import type { LoanModalPayload, ModalProps } from '../../types/modal.types';

import { usePayStatement } from '../../../__generated__/rest/api';
import { createFormComponent } from '../../utils/create-form-component';

const schema = z.object({
  amountCents: z.number().min(1, 'Amount is required'),
  plaidAccountId: z.string().trim().min(1, 'Funding account is required'),
});

export const PayStatementComponent = ({ payload, api }: ModalProps<LoanModalPayload>) => {
  const { loanId } = payload;
  const { handleMutation } = useMutationWithToast();

  const payStatementMutation = usePayStatement();

  const handleSubmit = (data: z.infer<typeof schema>) => {
    void handleMutation({
      mutation: () =>
        payStatementMutation.mutateAsync({
          data: {
            loanId,
            amountCents: data.amountCents,
            plaidAccountId: data.plaidAccountId,
          },
        }),
      extractSuccess: (result) => !!result?.id,
      onSuccess: () => api.close(),
      debugLabel: 'pay-statement',
      toast: {
        success: 'Statement paid successfully',
        error: 'Failed to pay statement',
        loading: 'Processing payment...',
      },
    });
  };

  const config: SmartFormConfig<z.infer<typeof schema>> = {
    fields: [
      {
        name: 'amountCents',
        label: 'Amount',
        placeholder: 'Enter amount',
        required: true,
        defaultValue: 0,
        component: CurrencyField,
        componentProps: {
          showCurrencySymbol: true,
        },
      },
      {
        name: 'plaidAccountId',
        label: 'Funding Account ID',
        type: 'text',
        placeholder: 'Plaid account id',
        required: true,
        helperText: 'The plaid account the repayment is pulled from.',
      },
    ],
    schema,
    defaultValues: {
      amountCents: 0,
      plaidAccountId: '',
    },
  };

  return createFormComponent({
    config,
    handleSubmit,
    loading: payStatementMutation.isPending,
  });
};
